<!--
Copyright (c) 2020 ForgeRock. All rights reserved.

This software may be modified and distributed under the terms
of the MIT license. See the LICENSE file for details.
-->

<template>
    <div class="fr-all-in-one-container">
        <idmUserDetails v-if="stages.idmUserDetails" :inline="true" :selfServiceDetails="selfServiceDetails" @toggle-keep-informed-visible="toggleKeepInformedVisibility"></idmUserDetails>
        <p class="registration-consent-text">
        <b-form-checkbox plain v-if="keepInformedVisible && (hklongman || isClinicalEurope || skillingPartner)" v-model="keepInformed"
                                 :value="true"
                                 :unchecked-value="false"
                                 @change="onChangeKeepInformed">
            {{$t("pages.selfservice.registration.regConsent.keepInformedConsent")}}
        </b-form-checkbox>
        </p>
        <p class="registration-consent-text">
        <b-form-checkbox plain v-if="enableReceiveMail" v-model="receiveMail"
                                 :value="true"
                                 :unchecked-value="false"
                                 :disabled="!keepInformed">
            {{$t("pages.selfservice.registration.regConsent.receiveMailText")}}
        </b-form-checkbox>
        </p>
        <p class="registration-consent-text">
            {{$t("pages.selfservice.registration.regConsent.text1")}}
            <a :href="termsUrl" target="_blank">{{revibe ? $t("pages.selfservice.registration.revibe.osa") : $t("pages.selfservice.registration.regConsent.termsAnchorText")}}</a> <span v-if="!hidePrivacyConsent">{{$t("pages.selfservice.registration.regConsent.text2")}}</span>
            <a :href="policyUrl" target="_blank" v-if="!hidePrivacyConsent"> {{$t("pages.selfservice.registration.regConsent.privacyAnchorText")}}</a>
        </p>
        <kbaSecurityAnswerDefinitionStage v-if="stages.kbaSecurityAnswerDefinitionStage" :inline="true" :selfServiceDetails="selfServiceDetails"></KBASecurityAnswerDefinitionStage>
        <Captcha v-if="selfServiceDetails.requirements.recaptcha" :self-service-details="selfServiceDetails" ref="recaptcha"></Captcha>

        <template v-if="stages.consent && selfServiceDetails.requirements && selfServiceDetails.requirements.consent">
            <b-modal ref="consentModal"
                     :title="$t('pages.selfservice.registration.consent.title')"
                     @hide="loading = false;">

                <Consent :inline="true" :selfServiceDetails="selfServiceDetails"></Consent>

                <b-form-checkbox plain v-model="consentCheck"
                                 :value="false"
                                 :unchecked-value="true">
                    {{$t("pages.selfservice.registration.consent.agreement")}}
                </b-form-checkbox>

                <div slot="modal-footer" class="w-100">
                    <b-btn :disabled="consentCheck" size="sm" class="float-right" variant="primary" @click="save">
                        {{$t("common.form.okay")}}
                    </b-btn>
                </div>
            </b-modal>
        </template>
        <!-- <fr-loading-button type="button" variant="primary" class="mt-2 mb-3 btn-lg btn-block"
                           :label="$t('common.form.cancel')"
                           :large="true"
                           @click="$router.go(-1)"></fr-loading-button> -->
        <fr-loading-button type="button" variant="primary"
                           :label="$t('common.form.createAccount')"
                           :loading="loading"
                           :large="true"
                           :useSpacing="false"
                           @click="submitForm"/>

        <TermsAndConditions v-if="stages.termsAndConditions && selfServiceDetails.requirements && selfServiceDetails.requirements.terms" :inline="true" :selfServiceDetails="selfServiceDetails"></TermsAndConditions>
   </div>
</template>

<script>
import _ from 'lodash';
import Captcha from '../common/Captcha';
import Consent from './Consent';
import idmUserDetails from './UserDetails';
import kbaSecurityAnswerDefinitionStage from './KBASecurityAnswerDefinitionStage';
import LoadingButton from '@/components/utils/LoadingButton';
import TermsAndConditions from './TermsAndConditions';
import moment from 'moment';
import { CLIENT_ID, CLINICAL_EUROPE_CONSENT, COMMON_CONSENT, HKLONGMAN_CONSENT } from '../../../utils/common';
import { getClientById, CLIENT } from '../../../lib/getClientById';
import { getConsentPolicyByClientId } from '../../../lib/api/getConsentPolicy';

window.gaEvent('Register', 'Register', 'Rendered', 'page rendered.');

/**
 * @description Selfservice stage that is used for combing multiple selfservice stages (User details, Captcha, KBA, Terms and Confitions and Consent)
 *
 **/
export default {
    name: 'All-In-One-Registration',
    props: {
        selfServiceDetails: { required: true }
    },
    components: {
        idmUserDetails,
        kbaSecurityAnswerDefinitionStage,
        TermsAndConditions,
        Consent,
        Captcha,
        'fr-loading-button': LoadingButton
    },
    data () {
        const clientId = this.getQueryStringValue('clientId') || getClientById();
        let setUFCForCE = false;
        if (clientId) {
            setUFCForCE = true;
        }
        this.getConsent(getClientById());
        let data = {
            stages: {},
            consentCheck: true,
            loading: false,
            keepInformedVisible: this.enableUPCForCE(clientId) || false,
            keepInformed: setUFCForCE || false,
            clinicalEuropeClientId: clientId,
            hidePrivacyConsent: false,
            enableReceiveMail: false,
            receiveMail: setUFCForCE,
            hklongman: getClientById() === CLIENT.HK_LONGMAN,
            revibe: getClientById() === CLIENT.REVIBE,
            isClinicalEurope: getClientById()?.includes('clinical-europe') || false,
            consents: {
                TERMS: 'https://loginstatic.pearson.com/html/PearsonEULA.html',
                PRIVACY_POLICY: 'https://loginstatic.pearson.com/html/PearsonPrivacyPolicy.html'
            },
            recaptchaToken: undefined,
            skillingPartner: getClientById() === CLIENT.SKILLING_PARTNER
        };

        _.each(this.selfServiceDetails.requirements.stages, (name) => {
            data.stages[name] = true;
        });

        return data;
    },
    methods: {
        async getConsent (clientId) {
            console.log('get consent called for clientId', clientId);
            const consents = await getConsentPolicyByClientId(clientId);
            this.consents = consents;
            return consents;
        },
        submitForm () {
            if (this.selfServiceDetails.requirements.recaptcha) {
                this.$refs.recaptcha.executeScore('register')
                    .then((token) => {
                        this.recaptchaToken = token;
                    }).catch((e) => {
                        console.log('Recaptcha error: ' + e);
                    }).finally(() => {
                        this.saveCheck();
                    });
            } else {
                this.saveCheck();
            }
        },

        getData () {
            let data = {};

            /* istanbul ignore next */
            _.each(this.$children, (child) => {
                let childData = {};

                if (child.getData) {
                    childData = child.getData();
                }

                data = _.merge(data, childData);
            });

            if (this.selfServiceDetails.requirements.consentEnabled) {
                data.consentGiven = 'true';
            }

            data.user.upc = getClientById() === CLIENT.KIBO ? false : this.keepInformed;
            if (this.recaptchaToken) {
                data.recaptchaToken = this.recaptchaToken;
            }

            const clientId = this.getQueryStringValue('clientId') || getClientById();
            if (clientId) {
                data.clientId = clientId;
            }

            return data;
        },

        saveCheck () {
            window.gaEvent('Register', 'Register', 'Submit', 'Submit button clicked.');
            this.loading = true;

            if (this.selfServiceDetails.requirements.consentEnabled) {
                this.isValid().then(() => {
                    this.$refs.consentModal.show();
                });
            } else {
                /* istanbul ignore next */
                this.isValid().then(() => {
                    this.save();
                });
            }
        },

        parseDOB (value) {
            if (!value) {
                return null;
            }

            const parsedDate = moment(value, 'MM-YYYY', true);
            const endOfMonthDay = parsedDate.endOf('month');
            const [month, year] = value.split('-');
            const dobWithDay = `${month}-${endOfMonthDay.format('DD')}-${year}`;
            return dobWithDay;
        },
        save () {
            const dob = this.parseDOB(this.getData().user.dob);
            const data = dob ? {
                ...this.getData(),
                user: {
                    ...this.getData().user,
                    dob
                }
            } : this.getData();

            this.$emit('advanceStage', data);
            window.gaEvent('Register', 'Register', 'Send', 'Sent request to backend.');
            this.loading = false;
        },

        isValid () {
            let childChecks = [],
                validPromise = new Promise((resolve, reject) => {
                    if (this.$children) {
                        _.each(this.$children, (child, index) => {
                            /* istanbul ignore next */
                            if (this.$children[index].isValid) {
                                childChecks.push(this.$children[index].isValid());
                            }
                        });
                    }

                    Promise.all(childChecks).then((results) => {
                        let validCheck = true;

                        _.each(results, (check) => {
                            /* istanbul ignore next */
                            if (check === false) {
                                validCheck = false;
                            }
                        });

                        if (validCheck) {
                            resolve({ 'success': true });
                        } else {
                            this.displayNotification('error', this.$t('pages.selfservice.registration.pleaseComplete'));
                            this.loading = false;
                        }
                    });
                });

            return validPromise;
        },
        toggleKeepInformedVisibility (isVisible) {
            this.keepInformedVisible = isVisible;

            if (this.keepInformedVisible && this.getData().user.c === 'US') {
                this.keepInformed = true;
            }
        },
        getQueryStringValue (name) {
            let url = decodeURIComponent(window.location.href);
            name = name.replace(/[[]/, '\\["').replace(/[\]]/, '\\]');
            let regexS = '[\\?&]' + name + '=([^&#]*)';
            let regex = new RegExp(regexS);
            let results = regex.exec(url);
            return results == null ? null : results[1];
        },
        hideConsent () {
            this.hidePrivacyConsent = true;
        },
        enableUPCForCE (clientId) {
            if (clientId === CLIENT.KIBO || clientId === CLIENT.HK_LONGMAN) {
                return false;
            } else if (clientId) {
                return true;
            }
        },
        enableReceiveMail (clientId) {
            if (clientId === CLIENT_ID.GERMAN) {
                return true;
            }
        },
        onChangeKeepInformed () {
            if (this.clinicalEuropeClientId === CLIENT_ID.GERMAN) {
                this.receiveMail = this.keepInformed;
            }
        }
    },
    computed: {
        termsUrl () {
            let clientId = this.clinicalEuropeClientId;
            if (this.hklongman) {
                return HKLONGMAN_CONSENT.TERMS;
            } else if (clientId) {
                if (clientId.startsWith('clinical-europe-')) {
                    if (clientId === CLIENT_ID.FRENCH) {
                        return CLINICAL_EUROPE_CONSENT.FR;
                    } else {
                        return `https://${window.location.host}/static/consent/${clientId}-terms.html`;
                    }
                } else {
                    return this.consents.TERMS;
                }
            } else if (this.consents) {
                return this.consents.TERMS;
            } else {
                return COMMON_CONSENT.TERMS;
            }
        },
        policyUrl () {
            let clientId = this.clinicalEuropeClientId;
            if (this.hklongman) {
                return HKLONGMAN_CONSENT.PRIVACY_POLICY;
            } else if (clientId) {
                if (clientId.startsWith('clinical-europe-')) {
                    if (clientId === CLIENT_ID.GERMAN || clientId === CLIENT_ID.SPANISH) {
                        return `https://${window.location.host}/static/consent/${clientId}-privacy.html`;
                    } else {
                        this.hideConsent();
                        return '';
                    }
                } else if (clientId === CLIENT.REVIBE) {
                    this.hideConsent();
                    return '';
                } else {
                    return this.consents.PRIVACY_POLICY;
                }
            } else if (this.consents) {
                return this.consents.PRIVACY_POLICY;
            } else {
                return COMMON_CONSENT.PRIVACY_POLICY;
            }
        }
    }
};
</script>

<style scoped>
/* .btn-primary:hover {
    color: #fff;
    background-color: #5498b3 !important;
    border-color: #0d5b79 !important;
    border-top-color: rgb(0, 98, 204);
    border-right-color: rgb(0, 98, 204);
    border-bottom-color: rgb(0, 98, 204);
    border-left-color: rgb(0, 98, 204);
}
.btn-primary {
    color: #fff;
    background-color: #0f6c90 !important;
    border-color: #0d5b79 !important;
    border-top-color: rgb(0, 98, 204);
    border-right-color: rgb(0, 98, 204);
    border-bottom-color: rgb(0, 98, 204);
    border-left-color: rgb(0, 98, 204);
} */
.btn-primary:focus, .btn-primary.focus {
    -webkit-box-shadow: 0 0 0 0.2rem rgba(84, 152, 179, 0.5) !important;
    box-shadow: 0 0 0 0.2rem rgba(84, 152, 179, 0.5) !important;
}

</style>
